import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../../services/api';
import LinkifiedText from '../common/LinkifiedText';
import { PublicPopup, popupText } from '../../utils/popupRules';

/**
 * Screen-Popup Darstellung (22.10.2026) – Karte, Vollbild, Bottom-Sheet. Gemeinsam für Admin-Vorschau und
 * Live-Anzeige im Web. Bilder: feste 16:9-Fläche; passt das Format nicht, wird das Bild ganz gezeigt und der Rest
 * mit dem geblurrten Bild gefüllt (Marcel, Entscheidung 5). 1 px türkiser Rand (Regel), Links anklickbar.
 */
const ACCENT = '#3fced1';

export interface PopupViewProps {
  popup: PublicPopup;
  lang?: string;
  isLight?: boolean;
  onClose?: () => void;
  onButton?: (which: 'primary' | 'secondary') => void;
  /** Vorschau im Admin: kein fixed-Overlay, sondern im Rahmen */
  embedded?: boolean;
}

/** Upload-URLs (/uploads/popups/…) liegen auf dem Backend-Host, nicht auf dartcloudapp.de (Marcel 22.10.: Bild lud nicht). */
export const popupImageSrc = (img: { url: string; urlLight: string | null }, isLight: boolean, base: string = api.defaults.baseURL ?? ''): string => {
  const chosen = isLight && img.urlLight ? img.urlLight : img.url;
  return chosen.startsWith('/') ? `${base.replace(/\/+$/, '')}${chosen}` : chosen;
};

export const ImageSlider: React.FC<{ images: PublicPopup['content']['images']; isLight: boolean; base?: string; rounded?: string }> = ({ images, isLight, base, rounded = 'rounded-t-2xl' }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => { if (index >= images.length) setIndex(0); }, [images.length, index]);
  if (images.length === 0) return null;
  const img = images[Math.min(index, images.length - 1)];
  const src = popupImageSrc(img, isLight, base);
  return (
    <div className={`relative w-full overflow-hidden ${rounded}`} style={{ aspectRatio: '16 / 9' }} data-testid="popup-image">
      {img.fit === 'contain-blur' && <div aria-hidden className="absolute inset-0 scale-110 bg-cover bg-center blur-xl" style={{ backgroundImage: `url("${src}")` }} />}
      <img src={src} alt={img.alt ?? ''} className={`relative h-full w-full ${img.fit === 'cover' ? 'object-cover' : 'object-contain'}`} draggable={false} />
      {images.length > 1 && (
        <>
          <button type="button" aria-label="Vorheriges Bild" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white" onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}><ChevronLeft size={18} /></button>
          <button type="button" aria-label="Nächstes Bild" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white" onClick={() => setIndex((i) => (i + 1) % images.length)}><ChevronRight size={18} /></button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5" data-testid="popup-dots">
            {images.map((_, i) => <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: i === index ? ACCENT : 'rgba(255,255,255,0.5)' }} />)}
          </div>
        </>
      )}
    </div>
  );
};

const PopupView: React.FC<PopupViewProps> = ({ popup, lang = 'de', isLight = false, onClose, onButton, embedded = false }) => {
  const c = popup.content;
  const title = popupText(c.title, c.titleEn, lang);
  const subtitle = popupText(c.subtitle, c.subtitleEn, lang);
  const description = popupText(c.description, c.descriptionEn, lang);
  const kind = popup.layout.kind;
  const bg = isLight ? 'bg-white text-gray-900' : 'bg-zinc-950 text-white';
  const muted = isLight ? 'text-gray-600' : 'text-gray-400';
  const closeBtn = popup.layout.showClose !== false && (
    <button type="button" aria-label="Schließen" data-testid="popup-close" onClick={onClose} className={`absolute right-3 top-3 z-10 rounded-full p-1.5 ${isLight ? 'bg-black/10 text-gray-800' : 'bg-white/10 text-white'}`}><X size={16} /></button>
  );
  const buttons = (c.primaryButton || c.secondaryButton) && (
    <div className={`flex flex-col gap-2 ${kind === 'fullscreen' ? 'sticky bottom-0 pt-3' : ''}`}>
      {c.primaryButton && <button type="button" data-testid="popup-primary" onClick={() => onButton?.('primary')} className="w-full rounded-full px-4 py-2.5 text-sm font-semibold text-black" style={{ backgroundColor: ACCENT }}>{popupText(c.primaryButton.label, c.primaryButton.labelEn, lang)}</button>}
      {c.secondaryButton && <button type="button" data-testid="popup-secondary" onClick={() => onButton?.('secondary')} className={`w-full rounded-full px-4 py-2.5 text-sm font-semibold ${isLight ? 'text-gray-700' : 'text-gray-200'}`}>{popupText(c.secondaryButton.label, c.secondaryButton.labelEn, lang)}</button>}
    </div>
  );
  const body = (
    <div className="flex flex-col gap-2 p-4">
      {title && <h2 className="text-lg font-bold leading-snug" data-testid="popup-title">{title}</h2>}
      {subtitle && <p className={`text-sm font-medium ${muted}`} data-testid="popup-subtitle">{subtitle}</p>}
      {description && <div className={`text-sm leading-relaxed ${isLight ? 'text-gray-800' : 'text-gray-200'}`} data-testid="popup-description"><LinkifiedText text={description} /></div>}
      {buttons}
    </div>
  );
  const border = { border: `1px solid ${ACCENT}` };

  if (kind === 'fullscreen') {
    return (
      <div className={`${embedded ? 'absolute' : 'fixed'} inset-0 z-[300] flex flex-col overflow-y-auto ${bg}`} role="dialog" aria-modal="true" data-testid="popup-fullscreen" style={embedded ? undefined : border}>
        {closeBtn}
        <ImageSlider images={c.images} isLight={isLight} rounded="" />
        <div className="flex-1">{body}</div>
      </div>
    );
  }
  if (kind === 'bottom-sheet') {
    return (
      <div className={`${embedded ? 'absolute' : 'fixed'} inset-0 z-[300] flex items-end ${popup.layout.backdrop === 'blur' ? 'backdrop-blur-sm bg-black/30' : 'bg-black/60'}`} data-testid="popup-sheet" onClick={popup.layout.dismissable ? onClose : undefined}>
        <div className={`relative w-full rounded-t-2xl ${bg}`} style={border} onClick={(e) => e.stopPropagation()}>
          {closeBtn}
          <ImageSlider images={c.images} isLight={isLight} />
          {body}
        </div>
      </div>
    );
  }
  return (
    <div className={`${embedded ? 'absolute' : 'fixed'} inset-0 z-[300] flex items-center justify-center p-4 ${popup.layout.backdrop === 'blur' ? 'backdrop-blur-sm bg-black/30' : 'bg-black/60'}`} data-testid="popup-card" onClick={popup.layout.dismissable ? onClose : undefined}>
      <div className={`relative w-full max-w-md overflow-hidden rounded-2xl ${bg}`} style={border} onClick={(e) => e.stopPropagation()}>
        {closeBtn}
        <ImageSlider images={c.images} isLight={isLight} />
        {body}
      </div>
    </div>
  );
};

export default PopupView;
